import { useState } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { Color } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';

import Paragraph, { ParagraphSmall } from '../../../../kakapo-ui-kit/components/common/Paragraph';
import DateService from '../../../../kakapo-ui-services/services/common/DateService';
import TrashIcon from '../../../../kakapo-ui-kit/assets/icons/TrashIcon';
import Spinner from '../../../../kakapo-ui-kit/components/common/Spinner';
import PopoverConfirm from '../../../../kakapo-ui-kit/components/popovers/PopoverConfirm';

import InboxTaskItemButton from './InboxTaskItemButton';
import CycleMomentInstructionModal from 'packages/kakapo-ui-kit/components/cycles/CycleMomentInstructionModal';

const InboxTaskItemRemoveButton = styled.button`
	margin: 0;
	padding: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: 16px;
	background-color: transparent;

	svg {
		margin-top: 3px;
	}

	:hover {
		background-color: ${(props) => props.theme.colors.slateBlue};
		svg path {
			fill: ${(props) => props.theme.colors.white};
		}
	}
`;

const Element = styled.div`
	width: 100%;
	padding: 12px 0;
	display: flex;
	flex-direction: row;
	align-items: stretch;
	cursor: pointer;

	${InboxTaskItemRemoveButton} {
		display: none;
	}

	&:hover {
		background-color: ${(props) => props.theme.colors.lightBlueLight};

		${InboxTaskItemRemoveButton} {
			display: block;
		}
	}

	&:hover {
		position: relative;
		width: calc(100% + 32px);
		left: -16px;
		padding-left: 16px;
		padding-right: 16px;
	}

	&:active {
		background-color: ${(props) => props.theme.colors.whiteBlue};
	}
`;

function InboxTaskDeadline(props: { task: graphql.InboxTask }) {
	const { t } = useTranslation();

	const deadline =
		props.task.type === graphql.InboxTaskType.FeedbackRequestPending
			? props.task.feedbackResponse?.dueDate
			: props.task.type === graphql.InboxTaskType.EvaluationSubmissionOpen
			? props.task.evaluationSubmission?.deadline
			: props.task.type === graphql.InboxTaskType.MomentStart
			? props.task.moment?.endDate
			: undefined;

	if (!deadline) {
		return null;
	}

	const isDue = moment(deadline).isBefore(moment());

	const formattedDeadline = moment(deadline).format('L');

	return (
		<>
			{' '}
			&mdash;{' '}
			<Color color={isDue ? 'red' : 'slateBlue'}>
				{t('inbox.task.deadline', {
					deadline: formattedDeadline,
				})}
			</Color>
		</>
	);
}

export default function InboxTaskItem({
	onClick,
	title,
	buttonTitle,
	date,
	task,
	instruction,
	refresh,
}: {
	onClick: () => void;
	title: string;
	buttonTitle: string;
	date: Date;
	task: graphql.InboxTask;
	instruction?: graphql.LocalizedAttributedString;
	refresh: () => void;
}) {
	const { t } = useTranslation();
	const [showConfirm, setShowConfirm] = useState<boolean>(false);
	const [showInstruction, setShowInstruction] = useState(false);

	const [archive, { loading }] = graphql.useArchiveInboxTaskMutation();
	const [deleting, setDeleting] = useState(false);

	const onArchive = () => {
		setShowConfirm(true);
	};

	const sendArchive = async (id: string) => {
		await archive({ variables: { input: { id } } });
		setDeleting(true);
		refresh();
	};

	return (
		<>
			<Element
				onClick={
					task.status === graphql.InboxTaskStatus.Open &&
					instruction &&
					moment(task.createdAt) > moment('2021-11-10')
						? () => setShowInstruction(true)
						: onClick
				}
				onMouseLeave={() => {
					setShowConfirm(false);
				}}
			>
				<Flex flexGrow={1} flexDirection="column" alignItems="flex-start">
					<Paragraph>{title}</Paragraph>
					<ParagraphSmall color="slateBlue">
						{DateService.daysAgo(moment(date).toDate(), new Date(), t)}
						<InboxTaskDeadline task={task} />
					</ParagraphSmall>
				</Flex>
				<Flex mr={1} alignItems="center" style={{ position: 'relative' }}>
					{loading || deleting ? (
						<Spinner isLoading size="12px" />
					) : (
						<>
							{showConfirm && (
								<PopoverConfirm
									title={t('inbox.task.remove.confirm.title')}
									description={t('inbox.task.remove.confirm.description')}
									confirmButtonTitle={t('inbox.task.remove.confirm.confirmButton.title')}
									cancelButtonTitle={t('inbox.task.remove.confirm.cancelButton.title')}
									onConfirm={(confirmed) => {
										if (confirmed) {
											sendArchive(task.id);
										} else {
											setShowConfirm(false);
										}
									}}
								/>
							)}
							<InboxTaskItemRemoveButton
								onClick={(event) => {
									event.stopPropagation();
									onArchive();
								}}
							>
								<TrashIcon color="slateBlue" />
							</InboxTaskItemRemoveButton>
						</>
					)}
				</Flex>
				<Flex pl={2} alignItems="center" flexShrink={0}>
					<Box style={{ position: 'relative' }}>
						<InboxTaskItemButton color="slateBlue">{buttonTitle}</InboxTaskItemButton>
					</Box>
				</Flex>
			</Element>
			{showInstruction && (
				<CycleMomentInstructionModal
					title={title}
					instruction={instruction}
					buttonTitle={buttonTitle}
					onCancel={() => setShowInstruction(false)}
					onStart={() => onClick()}
				/>
			)}
		</>
	);
}
